import React, { createContext, useContext, useEffect, useMemo } from "react";
import { ApplicationInsights, ITelemetryItem } from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { useHistory } from "react-router-dom";

// 1. Setup the Plugin and Key
const reactPlugin = new ReactPlugin();
const instrumentationKey = process.env.REACT_APP_APPINSIGHTS_INSTRUMENTATIONKEY;

// 2. Define the Context Type (It holds the Core Instance, not just the plugin)
const AppInsightsContext = createContext<ApplicationInsights | null>(null);

// 3. Global variable to hold the instance (for non-React usage if needed)
let appInsightsInstance: ApplicationInsights | null = null;

export const AppInsightsContextProvider = ({ children }: { children: React.ReactNode }) => {
    const history = useHistory();

    // Initialize AppInsights only once
    const appInsights = useMemo(() => {
        // Check if already initialized to prevent duplicates
        if (appInsightsInstance) return appInsightsInstance;

        // Logic: Only initialize if not in development (OR if you want to test locally, remove the check)
        if (process.env.REACT_APP_ENV !== "development" && instrumentationKey) {
            const instance = new ApplicationInsights({
                config: {
                    instrumentationKey: instrumentationKey,
                    extensions: [reactPlugin],
                    extensionConfig: {
                        [reactPlugin.identifier]: { history: history },
                    },
                    // Optional: Add telemetry initializers here
                },
            });

            instance.loadAppInsights();
            appInsightsInstance = instance;
            return instance;
        }

        return null;
    }, [history]);

    return (
        <AppInsightsContext.Provider value={appInsights}>
            {children}
        </AppInsightsContext.Provider>
    );
};

// 4. Custom Hook for usage in components
export const useAppInsights = () => {
    const context = useContext(AppInsightsContext);

    // SAFETY FALLBACK:
    // If we are in Dev (context is null), return a dummy object
    // so your components don't crash when calling .trackTrace
    if (!context) {
        return {
            trackEvent: (args: any) => console.log('[Dev AI] trackEvent:', args),
            trackTrace: (args: any) => console.log('[Dev AI] trackTrace:', args),
            trackException: (args: any) => console.error('[Dev AI] trackException:', args),
            trackPageView: (args: any) => console.log('[Dev AI] trackPageView:', args),
            trackMetric: (args: any) => console.log('[Dev AI] trackMetric:', args),
        } as unknown as ApplicationInsights;
    }

    return context;
};

export { AppInsightsContext };
